'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  parseHex,
  rgbToHex,
  rgbToHsl,
  formatRgbString,
  formatHslString,
} from '@/lib/browser-color-tools-extra';

type EyeDropperResult = { sRGBHex: string };
type EyeDropperInstance = { open: () => Promise<EyeDropperResult> };
type EyeDropperCtor = new () => EyeDropperInstance;

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserColorPicker() {
  const [color, setColor] = useState('#2563EB');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [hasEyeDropper, setHasEyeDropper] = useState(false);

  useEffect(() => {
    // Feature-detect after mount to avoid an SSR/client hydration mismatch.
    if (typeof window !== 'undefined' && 'EyeDropper' in window) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasEyeDropper(true);
    }
  }, []);

  const rgb = useMemo(() => parseHex(color), [color]);

  const values = useMemo(() => {
    if (!rgb) return null;
    const hsl = rgbToHsl(rgb);
    return {
      hex: rgbToHex(rgb),
      rgb: formatRgbString(rgb),
      hsl: formatHslString(hsl),
    };
  }, [rgb]);

  async function handleCopy(label: string, value: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  async function handleEyeDropper() {
    if (typeof window === 'undefined' || !('EyeDropper' in window)) return;
    try {
      const Ctor = (window as unknown as { EyeDropper: EyeDropperCtor }).EyeDropper;
      const instance = new Ctor();
      const picked = await instance.open();
      setColor(picked.sRGBHex.toUpperCase());
    } catch {
      // User cancelled the eyedropper; ignore.
    }
  }

  const rows: Array<{ label: string; value: string }> = values
    ? [
        { label: 'HEX', value: values.hex },
        { label: 'RGB', value: values.rgb },
        { label: 'HSL', value: values.hsl },
      ]
    : [];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Color</span>
        <h2 className="tool-live-title">Color picker</h2>
        <p className="tool-live-subtitle">
          Pick a color, or sample one from anywhere on screen, and copy HEX, RGB, and HSL.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Choose a color</span>
              <input
                aria-label="Color picker"
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                style={{
                  width: '100%',
                  height: '3rem',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                }}
                type="color"
                value={color.match(/^#[0-9a-fA-F]{6}$/) ? color : '#2563EB'}
              />
            </label>
            <label className="tool-live-field">
              <span>HEX value</span>
              <input
                onChange={(e) => {
                  const next = e.target.value.trim();
                  setColor(next.startsWith('#') ? next.toUpperCase() : `#${next.toUpperCase()}`);
                }}
                placeholder="#2563EB"
                type="text"
                value={color}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            {hasEyeDropper ? (
              <button className="button button-secondary" onClick={handleEyeDropper} type="button">
                Sample from screen
              </button>
            ) : null}
          </div>
          {!hasEyeDropper ? (
            <p className="tool-live-panel-note">
              Screen sampling (EyeDropper) is not supported in this browser. Use the color box
              above.
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              background: values ? values.hex : '#2563EB',
              borderRadius: '12px',
              height: '120px',
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          {values ? (
            <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
              {rows.map((row) => (
                <div
                  key={row.label}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span style={{ minWidth: '3rem', fontWeight: 600, fontSize: '0.85rem' }}>
                    {row.label}
                  </span>
                  <code
                    style={{
                      flex: 1,
                      padding: '0.4rem 0.6rem',
                      borderRadius: '8px',
                      background: 'rgba(0,0,0,0.06)',
                      fontSize: '0.9rem',
                    }}
                  >
                    {row.value}
                  </code>
                  <button
                    className="button button-secondary"
                    onClick={() => handleCopy(row.label, row.value)}
                    type="button"
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="status-message" style={{ color: '#dc3545' }}>
              Enter a valid 6-digit hex color.
            </p>
          )}
          <p className="tool-live-panel-note">All values computed locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
